import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../AuthProvider';
import { supabase } from '../../../lib/supabase';
import logger from '../../../utils/logger';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const PromptCard = ({ currentSynopsis, details, projectId, onRegenerateComplete }) => {
  const { token, user } = useAuth();
  const [isEditingPrompt, setIsEditingPrompt] = useState(false);
  const [editedPrompt, setEditedPrompt] = useState('');
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [userName, setUserName] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  
  const prompt = currentSynopsis?.prompt || details?.prompt;
  
  // Check if user can view/edit the prompt (only PM and admin)
  const canViewPrompt = user?.role === 'admin' || user?.role === 'pm' || 
                         user?.app_metadata?.role === 'admin' || user?.app_metadata?.role === 'pm';
  
  useEffect(() => {
    if (prompt) {
      logger.log('Prompt data loaded:', prompt);
    }
  }, [prompt, user]);
  
  // Update edited prompt when prompt changes
  useEffect(() => {
    if (prompt?.prompt_text) {
      setEditedPrompt(prompt.prompt_text);
    }
  }, [prompt?.prompt_text]);
  
  // Fetch user name from user_id
  useEffect(() => {
    const fetchUserName = async () => {
      if (!prompt?.user_id) {
        logger.log('⚠️ No user_id in prompt, fetching from synopsis_prompts table...');
        
        // If user_id is not in the prompt object, fetch it from Supabase
        if (prompt?.id) {
          try {
            const { data, error } = await supabase
              .from('synopsis_prompts')
              .select('user_id')
              .eq('id', prompt.id)
              .single();
            
            if (error) {
              logger.error('Error fetching user_id from synopsis_prompts:', error);
              return;
            }
            
            if (data?.user_id) {
              logger.log('✅ Found user_id from synopsis_prompts:', data.user_id);
              // Now fetch the user name with this user_id
              await fetchUserNameById(data.user_id);
            }
          } catch (error) {
            logger.error('Error fetching user_id:', error);
          }
        }
        return;
      }
      
      // If we have user_id, fetch the user name
      await fetchUserNameById(prompt.user_id);
    };
    
    const fetchUserNameById = async (userId) => {
      try {
        // Check if it's the current user
        if (user?.id === userId) {
          const displayName = user.name || user.email?.split('@')[0] || 'You';
          setUserName(`${displayName} (you)`);
          logger.log('✅ Current user:', displayName);
          return;
        }
        
        // Try to get creator name from project/details props if available
        // details.project is the enriched project object returned by the backend
        const creatorName = details?.project?.created_by_name || details?.created_by_name || details?.creatorName;
        const projectUserId = details?.project?.user_id || details?.user_id || details?.userId;
        
        if (creatorName && projectUserId === userId) {
          setUserName(creatorName);
          logger.log('✅ User name from props:', creatorName);
          return;
        }

        // Fetch user profile from Supabase auth.users metadata
        // Note: getUserById usually requires admin privileges, so this will likely fail for regular users
        const { data, error } = await supabase.auth.admin.getUserById(userId);
        
        if (error) {
          logger.log('⚠️ Cannot fetch from auth.users (admin only)');
          // Fallback: show truncated user ID or "Other User"
          setUserName(`User ${userId.substring(0, 8)}`);
          return;
        }
        
        if (data?.user) {
          const displayName = data.user.user_metadata?.name || 
                             data.user.user_metadata?.full_name || 
                             data.user.email?.split('@')[0] || 
                             'Unknown User';
          setUserName(displayName);
          logger.log('✅ User name from auth.users:', displayName);
        }
      } catch (error) {
        logger.error('Error fetching user name:', error);
        setUserName(`User ${userId.substring(0, 8)}`);
      }
    };
    
    fetchUserName();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prompt?.user_id, prompt?.id, user]);
  
  const handleEditPrompt = () => {
    setIsEditingPrompt(true);
    setEditedPrompt(prompt?.prompt_text || '');
  };
  
  const handleCancelEdit = () => {
    setIsEditingPrompt(false);
    setEditedPrompt(prompt?.prompt_text || '');
  };
  
  const handleReRunClick = () => {
    setShowConfirmModal(true);
  };
  
  const handleConfirmReRun = async () => {
    setShowConfirmModal(false);
    await handleRegenerateSynopsis();
  };
  
  const handleRegenerateSynopsis = async () => {
    if (!projectId) return;
    logger.log('Project ID:', projectId);
    setIsRegenerating(true);
    try {
      if (!token) {
        alert('Please log in to regenerate synopsis');
        setIsRegenerating(false);
        return;
      }
      
      let promptToUse = null;
      let savedPromptId = null;
      
      if (isEditingPrompt) {
        const trimmedPrompt = (editedPrompt || '').trim();
        if (!trimmedPrompt) {
          alert('Prompt cannot be empty');
          setIsRegenerating(false);
          return;
        }
        
        try {
          const saveResponse = await fetch(`${API_BASE}/synopsis/prompt`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ prompt: trimmedPrompt })
          });
          
          if (!saveResponse.ok) {
            const errorData = await saveResponse.json();
            throw new Error(errorData.detail || 'Failed to save prompt');
          }
          
          const saveResult = await saveResponse.json();
          promptToUse = trimmedPrompt;
          savedPromptId = saveResult.id;
          logger.log('✅ Prompt saved as new version:', saveResult);
        } catch (saveError) {
          logger.error('Error saving prompt:', saveError);
          alert(`Failed to save prompt: ${saveError.message}. Please try again.`);
          setIsRegenerating(false);
          return;
        }
      } else {
        promptToUse = prompt?.prompt_text;
      }
      
      if (!promptToUse) {
        alert('No prompt available to use for regeneration');
        setIsRegenerating(false);
        return;
      }
      
      const response = await fetch(`${API_BASE}/synopsis/regenerate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          project_id: projectId,
          prompt: promptToUse,
          prompt_id: savedPromptId
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to regenerate synopsis');
      }
      
      const result = await response.json();
      logger.log('✅ Synopsis regeneration started, job ID:', result.job_id);
      
      // Wait for completion
      await pollJobStatus(result.job_id);
      
      if (isEditingPrompt) {
        setIsEditingPrompt(false);
      }
      
      if (onRegenerateComplete) {
        await onRegenerateComplete();
      }
      
      alert('Synopsis regenerated successfully!');
    } catch (error) {
      logger.error('Error regenerating synopsis:', error);
      alert(`Failed to regenerate synopsis: ${error.message}`);
    } finally {
      setIsRegenerating(false);
    }
  };
  
  const pollJobStatus = async (jobId) => {
    let jobCompleted = false;
    let pollCount = 0;
    const maxPolls = 60;
    const pollInterval = 1000;
    
    while (!jobCompleted && pollCount < maxPolls) {
      await new Promise(resolve => setTimeout(resolve, pollInterval));
      
      try {
        const jobResponse = await fetch(`${API_BASE}/job/${jobId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (jobResponse.ok) {
          const jobData = await jobResponse.json();
          logger.log(`📊 Regeneration job status: ${jobData.status}`);
          
          if (jobData.status === 'completed') {
            jobCompleted = true;
            break;
          } else if (jobData.status === 'failed') {
            throw new Error(jobData.error || 'Regeneration failed');
          }
        }
      } catch (jobError) {
        logger.error('Error polling job:', jobError);
      }
      
      pollCount++;
    }
  };
  
  // Hide the entire prompt card for reviewers and regular users
  if (!canViewPrompt) {
    return null;
  }
  
  if (!prompt) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-md font-semibold text-gray-900 mb-4">User Prompt</h2>
        <div className="text-center py-8">
          <p className="text-gray-600 text-sm">No prompt available</p>
        </div>
      </div>
    );
  }
  
  return (
    <>
      <div className="bg-white dark:bg-neutral-950 rounded-lg border border-gray-200 dark:border-neutral-800 p-2">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h2 className="text-md font-semibold text-gray-900 dark:text-neutral-100">Synopsis User Prompt</h2>
            <div>
              <div className="flex items-center gap-3 mt-1 text-xs text-gray-600 dark:text-neutral-400">
                {prompt?.version && (
                  <span>Version: {prompt.version}</span>
                )}
                {prompt?.updated_at && (
                  <span>Updated: {new Date(prompt.updated_at).toLocaleDateString()}</span>
                )}
                {userName && (
                  <span>By: {userName}</span>
                )}
              </div>
            </div>
          </div>
          
        </div>
      
      {isEditingPrompt ? (
        <div>
          <textarea
            value={editedPrompt}
            onChange={(e) => setEditedPrompt(e.target.value)}
            className="w-full h-48 p-3 border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-gray-900 dark:text-neutral-100 rounded text-xs focus:ring-2 focus:ring-sky-500 focus:border-sky-500 resize-none"
            placeholder="Enter your prompt here..."
          />
          <div className="flex items-center gap-3 mt-1 text-xs text-gray-600 dark:text-neutral-400">
            Regenerating will reset the current synopsis version and create a new one.
          </div>
          <div className="flex items-center justify-end space-x-2 mt-3">
            <button
              onClick={handleCancelEdit}
              className="px-3 py-1 text-xs font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded transition-colors"
            >
              CANCEL
            </button>
            <button
              onClick={handleReRunClick}
              disabled={isRegenerating}
              className="bg-sky-600 hover:bg-sky-700 text-white px-3 py-1 rounded text-xs font-medium transition-colors disabled:opacity-50"
            >
              {isRegenerating ? 'Saving & Re-Generat...' : 'Save & Re-Generate'}
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-gray-50 dark:bg-neutral-900 rounded p-4 border border-gray-200 dark:border-neutral-700">
          <p className="text-xs text-gray-800 dark:text-neutral-200 whitespace-pre-wrap leading-relaxed">
            {prompt.prompt_text || 'No prompt text available'}
          </p>
        </div>
      )}
        <div className="flex items-center justify-end space-x-2 mt-3">
          {!isEditingPrompt && (
            <button
              onClick={handleEditPrompt}
              className="bg-sky-600 hover:bg-sky-700 text-white px-3 py-1 rounded text-xs font-medium transition-colors"
            >
              Edit
            </button>
          )}
        </div>
      </div>
      
      
      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-neutral-900 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-neutral-100 mb-4">
                Confirm Synopsis Regeneration
              </h3>
              <div className="mb-6">
                <p className="text-sm text-gray-700 dark:text-neutral-300 mb-3">
                  You are about to regenerate the synopsis using the current prompt:
                </p>
                <div className="bg-gray-50 dark:bg-neutral-800 rounded p-3 border border-gray-200 dark:border-neutral-700 max-h-32 overflow-y-auto">
                  <p className="text-xs text-gray-800 dark:text-neutral-200 whitespace-pre-wrap">
                    {prompt?.prompt_text || 'No prompt text available'}
                  </p>
                </div>
                <div className="mt-3 text-xs text-gray-600 dark:text-neutral-400">
                  <p>• A new synopsis version will be created</p>
                  <p>• The current version will be preserved</p>
                  <p>• This process may take a few moments</p>
                </div>
              </div>
              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-neutral-300 bg-gray-200 dark:bg-neutral-700 hover:bg-gray-300 dark:hover:bg-neutral-600 rounded transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmReRun}
                  className="px-4 py-2 text-sm font-medium text-white bg-sky-600 hover:bg-sky-700 rounded transition-colors"
                >
                  Confirm & Re-Generate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PromptCard;

